/**
 * Projects (Recent Works) — "Selected Work" portfolio section.
 *
 * A uniform two-column showcase grid. Every card has the same structure:
 * a browser-framed screenshot anchored to its top edge, then context, title,
 * summary, an optional hard result, the stack, and the live link.
 *
 * Two deliberate rules, both reactions to the previous bento version:
 *  1. Screenshots are never centre-cropped. They are top-aligned inside a
 *     fixed frame so each site's hero — the part that identifies it — stays
 *     readable instead of becoming an unreadable fragment.
 *  2. Nothing that proves competence hides behind :hover. Role, year, stack,
 *     result and link are all visible at rest; hover is emphasis only.
 */
import { useEffect, useRef, useState } from "react";
import "../ComponentsStyling/RecentProjects.css";

// Sets `inView` true the first time the element scrolls into view, then disconnects.
function useInView(threshold = 0.04) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* Project entries, ordered strongest first.

   `result` holds a HARD, verifiable number or nothing — never a tech name.
   It renders as the last item of the meta line rather than as its own block,
   so a project without one costs no vertical space and every card stays the
   same height. Summaries are written to two lines at card width for the
   same reason. */
const PROJECTS = [
  {
    id: "ieee-cis",
    title: "IEEE CIS UJ",
    summary:
      "The chapter's public site and events hub — designed, built and maintained as sole developer across two years.",
    context: "Organizational",
    role: "Frontend Lead",
    year: "2024–2026",
    result: "1,100+ visitors",
    stack: ["React", "CSS", "Responsive"],
    img: "/images/projects/ieeecis.png",
    url: "https://ieeecis-uj.org/",
    domain: "ieeecis-uj.org",
    live: true,
  },
  {
    id: "ai-expo",
    title: "AI-Expo",
    summary:
      "Official platform for one of Jordan's largest AI events, held under the Ministry of Digital Economy & Entrepreneurship.",
    context: "Event platform",
    role: "Sole Developer",
    year: "2025",
    result: null,
    stack: ["React", "CSS"],
    img: "/images/projects/aiExpo.jpg",
    url: "https://aiexpo26.com/",
    domain: "aiexpo26.com",
    live: true,
  },
  {
    id: "pathly",
    title: "Pathly",
    summary:
      "A knowledge atlas for mapping what you already know and seeing what comes next, with an Ask Pathly reference desk.",
    context: "Personal product",
    role: "Design & Development",
    year: "2026",
    result: null,
    stack: ["React", "Framer Motion", "React Router"],
    img: "/images/projects/pathly.jpg",
    url: "https://pathly-knowledge.netlify.app/",
    domain: "pathly-knowledge.netlify.app",
    live: true,
  },
  {
    id: "econova",
    title: "EcoNova",
    summary:
      "Business analytics dashboards for revenue, expenses and CO₂, with secure auth and automated time-series forecasting.",
    context: "Full product · solo",
    role: "Developer",
    year: "2026",
    result: null,
    stack: ["Flask", "MongoDB", "JavaScript"],
    img: "/images/projects/EcoNova.jpeg",
    url: "https://github.com/KhaledRaed/EcoNova",
    domain: "github.com/KhaledRaed/EcoNova",
    live: false,
  },
  {
    id: "smartcell",
    title: "SmartCell",
    summary:
      "Freelance corporate site taken from design to production for a telecom and fiber-optics contractor.",
    context: "Freelance client",
    role: "Freelance Developer",
    year: "2025",
    result: null,
    stack: ["HTML", "CSS", "JavaScript"],
    img: "/images/projects/SmartCell.png",
    url: "https://ses-jo.com/",
    domain: "ses-jo.com",
    live: true,
  },
];

// Header stats. Derived from the data so they cannot drift out of sync with it.
const LIVE_COUNT = PROJECTS.filter(p => p.live).length;
const REACHED = PROJECTS
  .filter(p => p.result)
  .reduce((sum, p) => sum + parseInt(p.result.replace(/\D/g, ""), 10), 0);

/* One project card.
   The title carries the only link; `.wk__link::after` stretches it over the
   whole card, so each card is a single tab stop whose accessible name is the
   project. The footer "Live site" is therefore presentational. */
function Card({ p, i }) {
  return (
    <article className="wk" style={{ "--i": i }}>
      <div className="wk__frame">
        <div className="wk__shot">
          <img
            src={p.img}
            alt={`${p.title} — ${p.context} project screenshot`}
            className="wk__img"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>

      <div className="wk__body">
        <p className="wk__meta">
          <span>{p.context}</span>
          <span className="wk__meta-dot" aria-hidden="true" />
          <span>{p.role}</span>
          <span className="wk__meta-dot" aria-hidden="true" />
          <span>{p.year}</span>
          {/* A hard number joins the meta line rather than claiming a block of
              its own, so cards with and without one stay the same height */}
          {p.result && (
            <>
              <span className="wk__meta-dot" aria-hidden="true" />
              <span className="wk__result">{p.result}</span>
            </>
          )}
        </p>

        <h3 className="wk__title">
          <a
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            className="wk__link"
          >
            {p.title}
            <span className="sr-only">
              {` — open ${p.live ? "live site" : "source on GitHub"}`}
            </span>
          </a>
        </h3>

        <p className="wk__summary">{p.summary}</p>

        <div className="wk__foot">
          {/* Stack set as one mono line, not boxed chips — it pairs with the
              domain opposite it and drops a row of decoration from the card */}
          <p className="wk__stack">{p.stack.join("  ·  ")}</p>
          {/* The real domain is the affordance: it names the destination and
              doubles as proof the work actually shipped. */}
          <span className="wk__cta" aria-hidden="true">
            {p.live ? p.domain : "GitHub"}
            <svg viewBox="0 0 14 14" fill="none">
              <path d="M2 12L12 2M12 2H5M12 2v7"
                stroke="currentColor" strokeWidth="1.7"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
      </div>
    </article>
  );
}

// Section wrapper: header, stat strip and the uniform work grid.
export default function Projects() {
  const [ref, inView] = useInView();
  const [showAll, setShowAll] = useState(false);

  return (
    <section className="prj" id="work" ref={ref}>
      <div className="prj__dots"    aria-hidden="true" />
      <div className="prj__blob--a" aria-hidden="true" />
      <div className="prj__blob--b" aria-hidden="true" />

      {/* Section header: eyebrow, headline and summary */}
      <div className={`prj__head${inView ? " prj__head--in" : ""}`}>
        <div className="prj__eyebrow">
          <span className="prj__ey-n">04</span>
          <span className="prj__ey-l" aria-hidden="true" />
          <span className="prj__ey-t">Selected Work</span>
        </div>

        <div className="prj__hl">
          <h2 className="prj__h2">
            <span style={{ "--d": "0s"   }}>Recent</span>
            <em   style={{ "--d": ".07s" }} className="prj__em">Works</em>
          </h2>

          {/* Real figures, derived from the data above — not decorative pills */}
          <dl className="prj__stats" style={{ "--d": ".14s" }}>
            <div className="prj__stat">
              <dt>Projects</dt>
              <dd>{PROJECTS.length}</dd>
            </div>
            <div className="prj__stat">
              <dt>Live in production</dt>
              <dd>{LIVE_COUNT}</dd>
            </div>
            <div className="prj__stat">
              <dt>People reached</dt>
              <dd>{REACHED.toLocaleString()}+</dd>
            </div>
          </dl>
        </div>
      </div>

      <div className={`prj__sep${inView ? " prj__sep--in" : ""}`} aria-hidden="true" />

      {/* One grid for every viewport; mobile collapses to a single column and
          hides everything past the third card until "View all" is pressed. */}
      <div
        className={`prj__grid${inView ? " prj__grid--in" : ""}${showAll ? " prj__grid--all" : ""}`}
      >
        {PROJECTS.map((p, i) => <Card key={p.id} p={p} i={i} />)}
      </div>

      {!showAll && (
        <button
          type="button"
          className="btn btn--secondary prj__more"
          onClick={() => setShowAll(true)}
        >
          View all {PROJECTS.length} projects
          <svg className="btn__icon btn__icon--down" viewBox="0 0 16 16"
            fill="none" aria-hidden="true">
            <path d="M8 3v10M4 9l4 4 4-4"
              stroke="currentColor" strokeWidth="1.7"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </section>
  );
}
